import React, { useEffect, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import { sort_rows, toggle_sort } from '../shared/use_sortable';
import { TOTALS_ROUNDING_NOTE, fmt_count } from '../format';
import { cell_text, totals_view } from './adapters';

const NUMBER_KINDS = ['money2', 'rupees', 'int', 'decimal', 'bool'];
const sort_type_for = (kind) => {
  if (NUMBER_KINDS.indexOf(kind) !== -1) return 'number';
  if (kind === 'datetime') return 'date';
  return 'text';
};

/*
 * the click-to-preview panel: the first rows of /reports/data?preview=1 with the whole report's
 * totals under them, and the draft banner.
 */
const PreviewPanel = ({ preview, on_close, on_retry }) => {
  const heading_ref = useRef(null);
  const [sort, setSort] = useState(null);
  const { title, state, table, error_text } = preview;

  // The panel opens below the cards; focus (and so the viewport) moves to it. A new report starts unsorted.
  useEffect(() => {
    if (heading_ref.current) heading_ref.current.focus();
    setSort(null);
  }, [title]);

  const columns = table
    ? table.columns.map((c) => ({
        key: c.key,
        label: c.label,
        align: ['money2', 'rupees', 'int', 'decimal'].indexOf(c.kind) !== -1 ? 'right' : undefined,
        sort_key: c.key,
        sort_type: sort_type_for(c.kind),
        render: (row) => cell_text(c.kind, row[c.key])
      }))
    : [];
  const rows = table ? sort_rows(table.rows.map((r, i) => ({ ...r, preview_row: i })), columns, sort) : [];
  const totals = table ? totals_view(table) : [];
  const on_sort = (key) => setSort((s) => toggle_sort(s, key));

  return (
    <section className="tp-card" aria-labelledby="tp-rp-preview-title">
      <div className="tp-card-hd">
        <span className="tp-card-hd-title" id="tp-rp-preview-title" tabIndex={-1} ref={heading_ref}>
          {title}
          {state === 'ready' ? (
            <span className="tp-card-hd-sub">
              {' '}
              Showing {fmt_count(rows.length)} of {fmt_count(table.total_rows)} rows. Download the
              report for every row.
            </span>
          ) : null}
        </span>
        <button type="button" className="tp-btn-ghost tp-rp-close" onClick={on_close}>
          Close preview
        </button>
      </div>
      <div className="tp-rp-preview-body">
        {state === 'loading' ? <CustomLoading /> : null}
        {state === 'error' ? <LoadFailed text={error_text} on_retry={on_retry} /> : null}
        {state === 'ready' && table.draft.is_draft ? (
          <div className="tp-banner tp-banner-warn" role="status">
            {table.draft.label}
          </div>
        ) : null}
        {state === 'ready' ? (
          <>
            <DataTable
              columns={columns}
              rows={rows}
              rowKey={(r) => r.preview_row}
              cardTitle={(r) => r.teacher_name || r.teacher_uuid || ''}
              emptyText="This report has no rows for the month."
              sort={sort}
              on_sort={on_sort}
            />
            {totals.length ? (
              <>
                <dl className="tp-rp-totals" aria-label="Report totals">
                  {totals.map((t) => (
                    <div key={t.key} className="tp-rp-total">
                      <dt>{t.label}</dt>
                      <dd>{t.text}</dd>
                    </div>
                  ))}
                </dl>
                <p className="tp-rp-note">{TOTALS_ROUNDING_NOTE}</p>
              </>
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  );
};

export default PreviewPanel;
